import { createContext, use, useCallback, useMemo, useRef } from 'react';
import { getActiveSidebarId, open } from '@/features/sidebar';
import { useAnalytics } from '@/services/analytics';

interface CopilotSidebarContextValue {
  openCopilot: (meta?: { triggerSource: string }) => void;
  handleToggle: (isOpen: boolean) => void;
}

const CopilotSidebarContext = createContext<CopilotSidebarContextValue | null>(null);

export function CopilotSidebarProvider({ children }: { children: React.ReactNode }) {
  const { track } = useAnalytics();
  const pendingSource = useRef<string | null>(null);

  const openCopilot = useCallback((meta?: { triggerSource: string }) => {
    // Only set the trigger source when the sidebar isn't already showing copilot.
    // Otherwise the source sits in the ref and leaks into the next open/close cycle.
    if (getActiveSidebarId() !== 'copilot') {
      pendingSource.current = meta?.triggerSource ?? null;
    }
    open('copilot');
  }, []);

  const handleToggle = useCallback(
    (isOpen: boolean) => {
      if (isOpen) {
        const source = pendingSource.current ?? 'unknown';
        pendingSource.current = null;
        track('aiAssistant', 'opened', { 'Trigger Source': source });
      } else {
        track('aiAssistant', 'closed', {});
      }
    },
    [track],
  );

  const value = useMemo(() => ({ openCopilot, handleToggle }), [openCopilot, handleToggle]);

  return <CopilotSidebarContext.Provider value={value}>{children}</CopilotSidebarContext.Provider>;
}

export function useCopilotSidebar() {
  const context = use(CopilotSidebarContext);
  if (context == null) {
    throw new Error('useCopilotSidebar must be used within CopilotSidebarProvider');
  }
  return context;
}
